<template>
  <div class="padding">
    <!-- Page header -->
    <div class="fg-va-head">
      <h5 class="_300 m-b-0">User Guideline</h5>
    </div>

    <h6 class="_300 m-b-md">
      <a
        :href="homeUrl"
        class="text-muted"
      >FAQ Guidelines</a>
      <span class="text-muted"> / </span>
      View All
    </h6>

    <div class="box">
      <div class="box-body">
        <div class="fg-va-toolbar">
          <multiselect
            v-model="selectedCategory"
            :options="categories"
            :show-labels="false"
            class="fg-va-select"
            label="name"
            track-by="id"
            placeholder="All categories"
            @input="resetAndFetch"
          />
          <div class="fg-va-search-wrap">
            <input
              v-model="query"
              :class="{ 'is-invalid': queryError }"
              class="form-control fg-va-search"
              type="text"
              placeholder="Search"
              @input="debouncedFetch"
            >
            <small
              v-if="queryError"
              class="text-danger"
            >{{ queryError }}</small>
          </div>
        </div>

        <div
          v-if="loading"
          class="text-center text-muted p-y-2"
        >
          Loading...
        </div>
        <div
          v-else-if="!faqGuidelines.length"
          class="text-center text-muted p-y-2"
        >
          No guidelines found.
        </div>
        <div
          v-else
          class="fg-va-grid"
        >
          <faq-guideline-portal-card
            v-for="item in faqGuidelines"
            :key="item.id"
            :item="item"
            @detail="openDetail"
          />
        </div>
      </div>
    </div>

    <footer class="dker p-a b-t">
      <div class="row">
        <div class="col text-center">
          <div
            v-if="loaded"
            class="VuePagination justify-content-center m-t-1"
          >
            <pagination
              ref="pagination"
              :chunk="5"
              :for="tableName"
              :per-page="limit"
              :records="faqGuidelineCount"
              :vuex="false"
              theme="bootstrap4"
              @paginate="setPage"
            />
          </div>
        </div>
      </div>
    </footer>

    <faq-guideline-detail
      ref="detail"
      :show-acknowledge="true"
      @acknowledged="onAcknowledged"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import FaqGuidelinePortalCard from './faq-guideline-portal-card.vue';
import FaqGuidelineDetail from './faq-guideline-detail.vue';

const Laroute = require('../../laroute');

export default {
    components: {
        FaqGuidelinePortalCard,
        FaqGuidelineDetail,
    },

    data() {
        return {
            categories: [],
            selectedCategory: null,
            query: '',
            page: 1,
            limit: 9,
            loading: false,
            loaded: false,
            tableName: 'faq-guideline-view-all',
            debounceTimer: null,
            homeUrl: Laroute.route('franchise.faq-guideline.home'),
        };
    },

    computed: {
        ...mapGetters('faqGuideline', ['faqGuidelines', 'faqGuidelineCount']),

        queryError() {
            return this.htmlTagsSearchError(this.query);
        },
    },

    created() {
        this.loadCategories();
        this.fetchList();
    },

    methods: {
        loadCategories() {
            this.$store
                .dispatch('faqGuideline/getFaqGuidelineCategories')
                .then(categories => {
                    this.categories = categories;
                });
        },

        fetchList() {
            // Don't fire a request the server will reject — a 422 here would
            // land in the catch below and show a generic error popup.
            if (this.queryError) {
                return;
            }

            const params = {
                type: 'guideline',
                category_id: this.selectedCategory
                    ? this.selectedCategory.id
                    : undefined,
                query: this.query || undefined,
                page: this.page,
                limit: this.limit,
            };

            this.loading = true;
            this.$store
                .dispatch('faqGuideline/getFaqGuidelines', params)
                .catch(() => {
                    this.swalError({ text: 'Unable to load guidelines.' });
                })
                .finally(() => {
                    this.loading = false;
                    this.loaded = true;
                });
        },

        setPage(page) {
            this.page = page;
            this.fetchList();
        },

        resetAndFetch() {
            this.page = 1;
            if (this.$refs.pagination) {
                this.$refs.pagination.setPage(1);
            }
            this.fetchList();
        },

        debouncedFetch() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => this.resetAndFetch(), 350);
        },

        openDetail(item) {
            this.$store
                .dispatch('faqGuideline/getFaqGuideline', item.id)
                .then(guideline => {
                    this.$refs.detail.show(guideline);
                });
        },

        // The acknowledge action already patched the row in the store (see
        // PATCH_FAQ_GUIDELINE), so the pill is correct immediately; the refetch
        // keeps counts and ordering honest.
        onAcknowledged() {
            this.fetchList();
        },
    },
};
</script>

<style scoped>
.fg-va-head {
    margin-bottom: 14px;
}

.fg-va-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 18px;
}
.fg-va-select {
    width: 240px;
    flex: 0 0 auto;
}
/* The wrapper carries the layout (it holds the input plus its validation
   message); the input just fills it. */
.fg-va-search-wrap {
    width: 260px;
    max-width: 50%;
    margin-left: auto;
    flex: 0 0 auto;
}
.fg-va-search {
    width: 100%;
}

.fg-va-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
}

@media (max-width: 991.98px) {
    .fg-va-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}
@media (max-width: 575.98px) {
    .fg-va-grid {
        grid-template-columns: 1fr;
    }
    .fg-va-toolbar {
        flex-wrap: wrap;
    }
    .fg-va-select,
    .fg-va-search-wrap {
        width: 100%;
        max-width: 100%;
        margin-left: 0;
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/faq_guideline/faq-guideline-view-all.vue